import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { getPageBlock, sendPageBlockMetric } from "../../common/api/pageApi";
import Loader from "../../shared/Ui/Loader/Loader";

function PageViewer() {
  const { id } = useParams<{ id: string }>();
  const [html, setHtml] = useState("");
  const [css, setCss] = useState("");
  const [js, setJs] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id) {
      loadPage(id);
    }
  }, [id]);

  // Обработка полноэкранного режима
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    const element = fullscreenContainerRef.current || containerRef.current;
    if (!element) return;

    try {
      if (!isFullscreen) {
        // Вход в полноэкранный режим
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if ((element as any).webkitRequestFullscreen) {
          await (element as any).webkitRequestFullscreen();
        } else if ((element as any).mozRequestFullScreen) {
          await (element as any).mozRequestFullScreen();
        } else if ((element as any).msRequestFullscreen) {
          await (element as any).msRequestFullscreen();
        }
      } else {
        // Выход из полноэкранного режима
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Ошибка при переключении полноэкранного режима:', error);
    }
  };

  const loadPage = async (pageId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const block = await getPageBlock(pageId);
      console.log("Загружен блок:", block);
      console.log("HTML длина:", block.html?.length);
      console.log("CSS длина:", block.css?.length);
      console.log("JS длина:", block.js?.length);
      
      if (!block.html && !block.css && !block.js) {
        throw new Error("Блок пустой или не найден");
      }
      
      setHtml(block.html || "");
      setCss(block.css || "");
      setJs(block.js || "");
    } catch (err: any) {
      console.error("Ошибка загрузки страницы:", err);
      setError(err?.message || err?.response?.data?.message || "Ошибка при загрузке страницы");
    } finally {
      setIsLoading(false);
    }
  };

  // Рендерим блок в контейнер
  useEffect(() => {
    if (!isLoading && !error && containerRef.current && id && html) {
      const container = containerRef.current;
      const blockId = id;
      
      console.log("Рендеринг блока:", { blockId, htmlLength: html.length, cssLength: css.length, jsLength: js.length });
      
      // Очищаем контейнер
      container.innerHTML = "";

      // Создаем iframe для безопасного выполнения кода
      const iframe = document.createElement("iframe");
      iframe.style.width = "100%";
      iframe.style.height = "100%";
      iframe.style.border = "none";
      iframe.sandbox.add("allow-scripts", "allow-same-origin");
      
      // Добавляем iframe в контейнер
      container.appendChild(iframe);
      
      // Функция для записи контента в iframe
      const writeContent = () => {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        
        if (!iframeDoc) {
          console.error("Не удалось получить доступ к документу iframe");
          return;
        }
        
        // Проверяем, является ли HTML полным документом
        const isFullDocument = /<!doctype\s+html>/i.test(html) || /<html[\s>]/i.test(html);
        
        let htmlContent = html;
        let headContent = '';
        
        if (isFullDocument) {
          // Извлекаем содержимое body из полного HTML документа
          const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
          if (bodyMatch && bodyMatch[1]) {
            htmlContent = bodyMatch[1];
            console.log("Извлечено содержимое body, длина:", htmlContent.length);
          }
          
          // Извлекаем содержимое head (для дополнительных мета-тегов)
          const headMatch = html.match(/<head[^>]*>([\s\S]*)<\/head>/i);
          if (headMatch && headMatch[1]) {
            headContent = headMatch[1]
              .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // Убираем встроенные стили
              .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ''); // Убираем скрипты из head
          }
        }

        // Скрипт для отслеживания метрик
        const metricsScript = `
          (function() {
            const blockId = "${blockId}";
            
            // Функция отправки метрики
            function sendMetric(element, action, metadata) {
              const elementType = element.tagName.toLowerCase();
              const elementId = element.id || element.getAttribute('data-id') || '';
              const elementText = element.textContent?.trim().substring(0, 100) || '';
              
              const metric = {
                blockId: blockId,
                elementId: elementId,
                elementType: elementType,
                elementText: elementText,
                action: action,
                timestamp: new Date().toISOString(),
                metadata: metadata || {}
              };
              
              // Отправляем метрику через postMessage родительскому окну
              if (window.parent && window.parent !== window) {
                window.parent.postMessage({
                  type: 'PAGE_BLOCK_METRIC',
                  data: metric
                }, '*');
              }
            }
            
            // Отслеживание кликов на всех интерактивных элементах
            document.addEventListener('click', function(e) {
              const target = e.target;
              const tagName = target.tagName.toLowerCase();
              
              // Отслеживаем клики на кнопках, ссылках, чипах и других интерактивных элементах
              if (tagName === 'button' || 
                  tagName === 'a' || 
                  target.closest('button') || 
                  target.closest('a') ||
                  target.closest('.chip') ||
                  target.closest('.btn') ||
                  target.hasAttribute('data-taste') ||
                  target.hasAttribute('data-action')) {
                
                const clickableElement = target.closest('button') || 
                                        target.closest('a') || 
                                        target.closest('.chip') || 
                                        target.closest('.btn') || 
                                        target;
                
                const metadata = {
                  href: clickableElement.href || clickableElement.getAttribute('href') || '',
                  dataTaste: clickableElement.getAttribute('data-taste') || '',
                  dataAction: clickableElement.getAttribute('data-action') || '',
                  className: clickableElement.className || ''
                };
                
                sendMetric(clickableElement, 'click', metadata);
              }
            }, true);
            
            // Отслеживание просмотра страницы
            if (document.readyState === 'complete') {
              sendMetric(document.body, 'view', {});
            } else {
              window.addEventListener('load', function() {
                sendMetric(document.body, 'view', {});
              });
            }
          })();
        `;

        iframeDoc.open();
        const fullHtml = `
          <!DOCTYPE html>
          <html lang="ru">
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              ${headContent}
              <style>${css}</style>
            </head>
            <body>
              ${htmlContent}
              <script>${js}</script>
              <script>${metricsScript}</script>
            </body>
          </html>
        `;
        
        console.log("Записываем HTML в iframe, длина:", fullHtml.length);
        iframeDoc.write(fullHtml);
        iframeDoc.close();
      };
      
      // Пытаемся записать контент сразу
      writeContent();
      
      // Если не получилось, ждем загрузки iframe
      iframe.onload = writeContent;

      // Обработчик сообщений от iframe для метрик
      const handleMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'PAGE_BLOCK_METRIC') {
          sendPageBlockMetric(event.data.data);
        }
      };

      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, [html, css, js, isLoading, error, id]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <p className="text-gray-500">ID страницы: {id}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative">
      {/* Кнопка полноэкранного режима */}
      <button
        onClick={toggleFullscreen}
        className="absolute top-4 right-4 z-50 p-3 bg-white/90 hover:bg-white border border-gray-300 rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center group"
        title={isFullscreen ? "Выйти из полноэкранного режима" : "Открыть на полный экран"}
        aria-label={isFullscreen ? "Выйти из полноэкранного режима" : "Открыть на полный экран"}
      >
        {isFullscreen ? (
          // Иконка выхода из полноэкранного режима
          <svg
            className="w-5 h-5 text-gray-700 group-hover:text-gray-900"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          // Иконка входа в полноэкранный режим
          <svg
            className="w-5 h-5 text-gray-700 group-hover:text-gray-900"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
            />
          </svg>
        )}
      </button>

      {/* Контейнер для контента */}
      <div ref={fullscreenContainerRef} className="w-full h-full">
        <div ref={containerRef} className="w-full h-full" />
      </div>
    </div>
  );
}

export default PageViewer;

